import type { FC, PropsWithChildren } from "react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  Customer,
  CustomerAddress,
  CustomerSimple,
} from "@commercekit/api";
import type {
  Locations
}  from "@commercekit/api/lib.server";
import { AddressType, CustomerType } from "@commercekit/api";
import { useFetcher, useNavigation } from "@remix-run/react";
import { AuthActions } from "~/lib/action/actions";
import { typedBoolean } from "@commercekit/shared";
import { useLocalePath } from "./useLocale";

interface CustomerState<T> {
  customer: null | T;
  name: string;
  loading: boolean;
  CustomerType: FC<{
    B2B: React.ReactNode;
    B2C: React.ReactNode;
    Contract?: React.ReactNode;
  }>;
  isContract: boolean;
}
const initValue = {
  customer: null,
  name: "",
  loading: false,
  CustomerType: () => <></>,
  isContract: false,
};
export const CustomerSimpleCtx =
  createContext<CustomerState<CustomerSimple>>(initValue);

export const CustomerSimpleCtxProvider = ({
  customer,
  children,
}: PropsWithChildren<{ customer: null | CustomerSimple }>) => {
  const name = useMemo(
    () =>
      customer
        ? [customer.firstName, customer.middleName, customer.lastName]
            .filter(typedBoolean)
            .join(" ")
        : "",
    [customer]
  );
  const { formData } = useNavigation();
  const loading = useMemo(
    () =>
      Object.values(AuthActions).includes(
        (formData?.get("_action") || "") as AuthActions
      ),
    [formData]
  );
  const isContract = useMemo(() => {
    return customer?.type === CustomerType.CONTRACT;
  }, [customer?.type]);
  const CustomerTypeContainer = useCallback(
    ({
      B2C,
      B2B,
      Contract,
    }: PropsWithChildren<{
      B2C: React.ReactNode;
      B2B: React.ReactNode;
      Contract?: React.ReactNode;
    }>) => {
      if (!customer) return <>{B2C}</>;
      switch (customer.type) {
        case CustomerType.B2C:
          return <>{B2C}</>;
        case CustomerType.B2B:
          return <>{B2B}</>;
        case CustomerType.CONTRACT:
          if (Contract) return <>{Contract}</>;
          return <>{B2B}</>;
        default:
          return <>{B2C}</>;
      }
    },
    [customer]
  );
  return (
    <CustomerSimpleCtx.Provider
      value={{
        customer,
        name,
        loading,
        CustomerType: CustomerTypeContainer,
        isContract,
      }}
    >
      {children}
    </CustomerSimpleCtx.Provider>
  );
};
export const useCustomerSimple = () => useContext(CustomerSimpleCtx);

export const CustomerCtx = createContext<{ customer: Customer | null }>({
  customer: null,
});

export const CustomerCtxProvider = ({
  children,
  customer,
}: PropsWithChildren<{ customer: null | Customer }>) => {
  return (
    <CustomerCtx.Provider value={{ customer }}>{children}</CustomerCtx.Provider>
  );
};

export const useCustomer = () => useContext(CustomerCtx);

export const useShippingAddress = () => {
  const { customer } = useCustomer();
  const shippingAddressList = useMemo(
    () => filterByType(customer?.addresses || [], AddressType.SHIPPING),
    [customer]
  );
  const shippingAddressDefault = useMemo(
    () => getDefault(shippingAddressList),
    [shippingAddressList]
  );
  return { shippingAddressList, shippingAddressDefault };
};

export const useBillingAddress = () => {
  const { customer } = useCustomer();
  const billingAddressList = useMemo(
    () => filterByType(customer?.addresses || [], AddressType.BILLING),
    [customer]
  );
  const billingAddressDefault = useMemo(
    () => getDefault(billingAddressList),
    [billingAddressList]
  );
  return { billingAddressList, billingAddressDefault };
};

export const useDeliveryLocations = (
  shippingAddress: CustomerAddress | null
) => {
  const [isLoadLocations, setLoadLocations] = useState(false);
  const { customer } = useCustomer();
  const fetcher = useFetcher<{ locations: Locations[] }>();
  const isLoading = useMemo(() => {
    return fetcher.state !== "idle";
  }, [fetcher]);
  const deliveryLocations = useMemo(() => {
    if (fetcher.data?.locations) {
      return fetcher.data.locations.map((item) => {
        return {
          name: `${item.name} (${item.street} ${item.houseNumber}, ${
            item.postalCode
          } ${item.city}${item.phone ? ", Tel:" + item.phone : ""})`,
          value: `${item.name}${item.id}`,
        };
      });
    }
    return [];
  }, [fetcher?.data]);
  const [paramsOfDeliveryLocations, setParamsOfDeliveryLocations] = useState({
    countryCode: "NL",
    postalCode: "",
  });
  useEffect(() => {
    setLoadLocations(true);
  }, [shippingAddress, paramsOfDeliveryLocations]);
  const localePath = useLocalePath();
  useEffect(() => {
    const search = new URLSearchParams("");
    if (isLoadLocations) {
      if (customer) {
        if (shippingAddress?.countryCode && shippingAddress?.postalCode) {
          search.set("countryCode", shippingAddress.countryCode);
          search.set(
            "postalCode",
            shippingAddress.postalCode.replace(/\s*/g, "")
          );
          fetcher.load(
            localePath(`/delivery-locations${"?" + search.toString()}`)
          );
        }
      } else {
        if (
          paramsOfDeliveryLocations.countryCode &&
          paramsOfDeliveryLocations.postalCode
        ) {
          search.set("countryCode", paramsOfDeliveryLocations.countryCode);
          search.set(
            "postalCode",
            paramsOfDeliveryLocations.postalCode.replace(/\s*/g, "")
          );
          fetcher.load(
            localePath(`/delivery-locations${"?" + search.toString()}`)
          );
        }
      }
      setLoadLocations(false);
    }
  }, [
    customer,
    fetcher,
    isLoadLocations,
    localePath,
    paramsOfDeliveryLocations,
    shippingAddress,
  ]);
  return {
    isLoading,
    deliveryLocations,
    setParamsOfDeliveryLocations,
  };
};

export const useCompanyShippingAddress = () => {
  const { customer } = useCustomer();
  const companyShippingAddressList = useMemo(
    () =>
      filterByCompanyAddressType(
        customer?.company?.addresses || [],
        AddressType.SHIPPING
      ),
    [customer]
  );
  return {
    companyShippingAddressList,
    companyShippingAddressDefault:
      customer?.company?.defaultShippingAddress ||
      companyShippingAddressList[0],
  };
};
export const useCompanyBillingAddress = () => {
  const { customer } = useCustomer();
  const companyBillingAddressList = useMemo(
    () =>
      filterByCompanyAddressType(
        customer?.company?.addresses || [],
        AddressType.BILLING
      ),
    [customer]
  );
  return {
    companyBillingAddressList,
    companyBillingAddressDefault:
      customer?.company?.defaultBillingAddress || companyBillingAddressList[0],
  };
};
export const useCompanyHeadquartersAddress = () => {
  const { customer } = useCustomer();
  const companyHeadquartersAddressList = useMemo(
    () =>
      filterByCompanyAddressType(
        customer?.company?.addresses || [],
        AddressType.HEADQUARTERS
      ),
    [customer]
  );
  return {
    companyHeadquartersAddressList,
    companyHeadquartersAddressDefault:
      customer?.company?.defaultHeadquartersAddress ||
      companyHeadquartersAddressList[0],
  };
};

function filterByType(list: CustomerAddress[], type: AddressType) {
  return list.filter((i) => i.type === type);
}

function filterByCompanyAddressType(
  list: CustomerAddress[],
  type: AddressType
) {
  return list.filter((i) => i.type === type);
}

function getDefault(list: CustomerAddress[]) {
  return list.find((i) => i.isDefault) || list[0];
}
